import React, { useEffect, useState } from "react";
import { discoverCollectionFields } from "../services/conditionQueryService";
import { CONDITION_OPS, UPDATE_TYPES } from "../constants";

export default function ConditionFilterForm({
  collectionName,
  allCollections,
  onCollectionChange,
  filter,
  onFilterChange,
  updateRequest,
  onUpdateChange,
  showUpdateSection = false,
}) {
  const [fields, setFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);

  useEffect(() => {
    if (!collectionName) return;
    let cancelled = false;
    setLoadingFields(true);
    discoverCollectionFields(collectionName)
      .then((f) => {
        if (!cancelled) setFields(f);
      })
      .catch(() => {
        if (!cancelled) setFields([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFields(false);
      });
    return () => {
      cancelled = true;
    };
  }, [collectionName]);

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Collection</label>
        <select
          className="w-full max-w-md rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
          value={collectionName}
          onChange={(e) => onCollectionChange(e.target.value)}
        >
          {allCollections.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Filter field</label>
          <input
            list="fa-field-list"
            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={filter.field}
            onChange={(e) => onFilterChange({ ...filter, field: e.target.value })}
            placeholder="Field name"
          />
          <datalist id="fa-field-list">
            {fields.map((f) => <option key={f} value={f} />)}
          </datalist>
          {loadingFields && <p className="text-[10px] text-slate-400">Loading fields…</p>}
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Operator</label>
          <select
            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={filter.op}
            onChange={(e) => onFilterChange({ ...filter, op: e.target.value })}
          >
            {CONDITION_OPS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Comparison value</label>
          <input
            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={filter.value}
            onChange={(e) => onFilterChange({ ...filter, value: e.target.value })}
            placeholder={filter.op === "in" ? "a, b, c" : "Value"}
          />
        </div>
      </div>

      {showUpdateSection && (
        <div className="grid gap-3 border-t border-slate-200 pt-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Update type</label>
            <select
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              value={updateRequest.updateType}
              onChange={(e) => onUpdateChange({ ...updateRequest, updateType: e.target.value })}
            >
              {UPDATE_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Target field</label>
            <input
              list="fa-field-list"
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              value={updateRequest.targetField}
              onChange={(e) => onUpdateChange({ ...updateRequest, targetField: e.target.value })}
            />
          </div>
          {["renameField", "moveField"].includes(updateRequest.updateType) && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">New field name</label>
              <input
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={updateRequest.renameTo || ""}
                onChange={(e) => onUpdateChange({ ...updateRequest, renameTo: e.target.value })}
              />
            </div>
          )}
          {updateRequest.updateType === "copyField" && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Source field</label>
              <input
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={updateRequest.sourceField || ""}
                onChange={(e) => onUpdateChange({ ...updateRequest, sourceField: e.target.value })}
              />
            </div>
          )}
          {!["deleteField", "setNull", "uppercase", "lowercase", "trim"].includes(updateRequest.updateType) && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">New value</label>
              <input
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={updateRequest.newValue ?? ""}
                onChange={(e) => onUpdateChange({ ...updateRequest, newValue: e.target.value })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
